import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2 } from 'lucide-react';
import type { SplitterOption } from '@uniticket/shared';

interface SplitterComboboxProps {
  label: string;
  /** The committed splitter reference ('' when nothing chosen yet). */
  value: string;
  onChange: (value: string) => void;
  /** Options shown before the user types anything (DMP's suggestion for this JVID). */
  prefillOptions: SplitterOption[];
  /** Options for the current search term. Only consulted once the user starts typing. */
  searchOptions: SplitterOption[];
  loading?: boolean;
  /** Debounced (300 ms) — called with the trimmed term, '' when the box is cleared. */
  onSearch: (query: string) => void;
  placeholder?: string;
  hint?: string;
  /** Shown when the prefill came back empty, so the user knows to type instead of being stuck. */
  emptyHint?: string;
}

/**
 * Search-enabled splitter picker used by both Splitter Swap fields.
 *
 * Adapted from the MANUAL_PATCH customer_reference dropdown in OpenOrderWizard, with two
 * deliberate differences:
 *  - the search is debounced (that one fires a request per keystroke);
 *  - a typed value that matches no option is still committed, with an amber hint. A Splitter Swap
 *    asserts DMP's records are wrong, so refusing a reference DMP doesn't know would block the
 *    exact case the ticket exists to report.
 */
export function SplitterCombobox({
  label,
  value,
  onChange,
  prefillOptions,
  searchOptions,
  loading = false,
  onSearch,
  placeholder,
  hint,
  emptyHint,
}: SplitterComboboxProps) {
  const { t } = useTranslation(['tickets', 'common']);
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  /** True once the user edits the box, so we know to prefer search results over the prefill. */
  const [dirty, setDirty] = useState(false);
  /** A debounce is scheduled or in flight — suppresses the "not in DMP" hint until it settles. */
  const [pending, setPending] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-sync only on a genuine external change (parent resetting the field, or the From field
  // being auto-selected). Ignore the echo of our own onChange, which would otherwise strip
  // whitespace from under the caret as the user types.
  useEffect(() => {
    setQuery((prev) => (prev.trim() === value ? prev : value));
    if (!value) setDirty(false);
  }, [value]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const term = query.trim();
  const options = dirty && term ? searchOptions : prefillOptions;
  // A reference is "known" if DMP returned it either as a suggestion or as a search hit.
  const isKnown = term.length > 0
    && (searchOptions.some((o) => o.customerReference === term)
      || prefillOptions.some((o) => o.customerReference === term));

  function handleChange(next: string) {
    setQuery(next);
    setDirty(true);
    setOpen(true);
    setPending(true);
    // Commit as we type: free text is a valid answer here, so the field must never be silently
    // empty just because the user didn't click a suggestion.
    onChange(next.trim());

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPending(false);
      onSearch(next.trim());
    }, 300);
  }

  function handleSelect(option: SplitterOption) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPending(false);
    setQuery(option.customerReference);
    onChange(option.customerReference);
    setOpen(false);
  }

  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label} <span className="text-brand-rose">*</span>
      </label>
      <input
        type="text"
        value={query}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        maxLength={100}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono"
      />
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}

      {loading && (
        <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>{t('tickets:openOrder.searching')}</span>
        </div>
      )}

      {open && !loading && options.length > 0 && (
        <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg">
          {options.map((option) => (
            <button
              key={option.customerReference}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(option)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0 font-mono flex items-center justify-between gap-2"
            >
              <span>{option.customerReference}</span>
              {(option.portPosition || option.portStatus) && (
                <span className="text-[11px] text-slate-400 font-sans whitespace-nowrap">
                  {[option.portPosition, option.portStatus].filter(Boolean).join(' · ')}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {!loading && !term && prefillOptions.length === 0 && emptyHint && (
        <p className="mt-2 text-xs text-slate-500">{emptyHint}</p>
      )}

      {isKnown && (
        <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span className="text-sm font-mono text-green-800">{term}</span>
        </div>
      )}
      {term && !isKnown && !loading && !pending && (
        <p className="mt-1 text-xs text-amber-600">{t('tickets:openOrder.splitterNotInDmpHint')}</p>
      )}
    </div>
  );
}
